import * as React from "react"
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap"
import { toast } from "react-toastify"
import { concat, of, Subject, Subscription } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import {isCompanyAdmin, isLocationAdmin, isPortalAdmin, isSiteAdmin, isUser} from "../../authentication/isAdminRole"
import { AppState } from "../../AppState"
import { connect } from "react-redux"
import UserInformation from "../../api/Administration/Model/UserInformation"
import AssociatedData from "../../api/Administration/Model/AssociatedData"
import { UserInformationObservable, UserNotificationInformationObservable } from "../../api/Administration/UserInformationGet"
import UserNotificationInfoUpdate, { UpdateUserDetailParams } from "../../api/Administration/UserNotificationInfoUpdate"
import CreateUser from "../../api/Administration/CreateUser"
import UserPasswordReset from "../../api/Administration/UserPasswordReset"
import UserRevoke from "../../api/Administration/UserRevoke"
import RoleOrderSecurityCheck from "../../api/Roles/RoleOrderSecurityCheck"
import ActionButton from "../ActionButton"
import ConfirmationModal from "../ConfirmationModal"
import NotificationSelection from "./Notification/NotificationSelection"
import AdditionalAssociatedDataModal, { AssociatedDataType, EmptyAssociatedData } from "./AdditionalAssociatedDataModal"
import AdditionalRoleModal from "./AdditionalRoleModal"
import OrderSecurityOverride from "../Administration/OrderSecurityOverride"
import FormatPhone from "../../formatters/FormatPhone"
import { APIError } from "../../api/ObservableFromFetch"
import { getUserNameForSelectedCompany, RoleIdForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import TimeZoneListGet from "../../api/Administration/TimeZoneListGet"
import TimeZoneData from "../../api/Administration/Model/TimeZoneData"
import Checkbox from "../Checkbox"
import { EmailRegexp, PhoneRegexp, MaxFailedLoginAttempts } from "../../config/Constants"
import { Typeahead } from "react-bootstrap-typeahead"
import Role from "../../api/Roles/Model/Role"
import { GetRolesToInvite } from "../../api/Roles/UserRolesGet"
import MaskedInput from "react-maskedinput"
import CheckExistingUser, {CheckUserExistsParameters, CheckUserExists, CheckUserExistsResult} from "../../api/Invitation/CheckExistingUser"
import RoleData from "../../api/Administration/Model/RoleData"
import AutoFilterCompany from '../../api/Companies/CompanyModel/AutoFilterCompany'
import CompanyInput from '../FormattedInputs/CompanyInput'
import AutoFilterCompaniesGet from '../../api/Companies/AutoFilterCompaniesGet';
import AutoFilterLocation from "../../api/Location/LocationModel/AutoFilterLocation"
import AutoFilterRegion from "../../api/Regions/Model/AutoFilterRegion"
import AutoFilterLocationsGet from "../../api/Location/AutoFilterLocationsGet"
import ResetLoginAttempts from "../../api/Administration/ResetLoginAttempts"
import RemoveDuplicateRoles from "../../api/Administration/RemoveDuplicateRoles"
import DeleteUser, { DeleteRole } from "../../api/Administration/DeleteUser"
import NotificationSettings from "../../api/Notifications/Model/NotificationSettings"

export enum UserInfoMode {
  Team = "Team",
  Personal = "Personal"
}

interface UserInfoProps {
  show: boolean
  LoginName?: string
  LoginAttempts?: number
  details?: UserInformation
  mode: UserInfoMode
  onClose?: (updated: boolean) => void
  participationId?: number
  locationId?: number
  timeZoneId?: number
  roleId?: number
  showNewUser?: boolean
}

interface UserInfoAuthProps {
  isLocationAdmin?: boolean
  isCompanyAdmin?: boolean
  isPortalAdmin?: boolean
  isSiteAdmin?: boolean
  isUser?: boolean
  UserName?: string
  PrimaryRoleID?: number
}

interface UserInfoValidity {
  emailValid: boolean
  phoneValid: boolean
  lastNameValid: boolean
  firstNameValid: boolean
  roleValid: boolean
  companyValid: boolean
}

export interface UserInfoData extends NotificationSettings {
  userInfo?: UserInformation
  firstName?: string
  lastName?: string
  email: string
  phone: string
  roleId: number
  roles?: Role[]
  inactive: boolean
  companies?: AutoFilterCompany[]
  locations?: AutoFilterLocation[]
}

interface UserInfoState extends UserInfoData, UserInfoValidity {
  showRevokeConfirmation: boolean
  showResetPasswordConfirmation: boolean
  showResetLoginAttemptsConfirmation: boolean
  showRemoveDuplicateRolesConfirmation: boolean
  showDeleteUserConfirmation: boolean
  showDeleteRoleConfirmation: boolean
  canShowDetailsToPerson: boolean
  showOrderSecurityOverride: boolean
  loading: boolean
  saving: boolean
  revoking: boolean
  resettingPassword: boolean
  performingResetLoginAttempts: boolean
  performingRemoveDuplicateRoles: boolean
  performingDeleteUser: boolean
  performingDeleteRole: boolean
  showAdditionalPhone: boolean
  showAdditionalRoles: boolean
  participationId?: number
  timeZoneId?: number
  timeZones: TimeZoneData[]
  emailChecked: boolean
  emailAvailable: boolean
  selectedCompany?: AutoFilterCompany
  selectedLocation?: AutoFilterLocation
}

export class UserInfo extends React.Component<UserInfoProps & UserInfoAuthProps, UserInfoState> {
  emptyData: UserInfoData = {
    userInfo: undefined,
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    roleId: 0,
    inactive: false,
    showNotifications: false,
    checkedNotificationServiceIDs: [],
    checkedNotificationEventIDs: [],
    checkedNotificationMethodIDs: [],
  }
  emptyState: UserInfoState = {
    ...this.emptyData,
    lastNameValid: true,
    firstNameValid: true,
    emailValid: true,
    phoneValid: true,
    roleValid: true,
    companyValid: true,
    showRevokeConfirmation: false,
    showResetPasswordConfirmation: false,
    showResetLoginAttemptsConfirmation: false,
    showRemoveDuplicateRolesConfirmation: false,
    showDeleteUserConfirmation: false,
    showDeleteRoleConfirmation: false,
    canShowDetailsToPerson: false,
    showOrderSecurityOverride: false,
    loading: false,
    saving: false,
    revoking: false,
    resettingPassword: false,
    performingResetLoginAttempts: false,
    performingRemoveDuplicateRoles: false,
    performingDeleteUser: false,
    performingDeleteRole: false,
    showAdditionalPhone: false,
    showAdditionalRoles: false,
    timeZoneId: this.props.timeZoneId,
    participationId: this.props.participationId,
    timeZones: [],
    emailChecked: false,
    emailAvailable: false,
    selectedCompany: undefined
  }
  state: UserInfoState = { ...this.emptyState }

  rolesTypeahead = React.createRef<Typeahead<Role>>()
  loadUserSubscription?: Subscription
  roleSecurityCheckSubscription?: Subscription
  saveSubscription?: Subscription
  userRevokeSubscription?: Subscription
  userPasswordResetSubscription?: Subscription
  userRolesSubscription?: Subscription
  existSubscription?: Subscription
  companiesSubscription?: Subscription
  locationsSubscription?: Subscription
  removeRoleSubscription?: Subscription

  loginName$ = new Subject<string>()
  roleID$ = new Subject<number>()

  timeZoneSubscription = new Subscription()

  componentDidMount() {
    this.timeZoneSubscription.add(
      TimeZoneListGet().subscribe(
        (results: TimeZoneData[]) => {
          this.setState({ timeZones: results })
        },
        () => { }
      )
    )

    this.userRolesSubscription = GetRolesToInvite().subscribe(data => this.setState({ roles: data }))

    const observable = this.props.mode === UserInfoMode.Personal ? UserInformationObservable : UserNotificationInformationObservable
    this.loadUserSubscription = this.loginName$.pipe(
      map(name => name.trim()),
      switchMap(name => concat(
        of({ loading: true }),
        observable(name, false).pipe(
          map(result => ( { loading: false, result: result } )),
          catchError(() => of({ loading: false }))
        )
      ))
    ).subscribe(
      (o: { loading: boolean, result?: UserInformation[] }) => {
        this.setState({ loading: o.loading })
        if (o.result) {
          this.setState({ ...this.mapUserInfo(o.result.find(i => i.ParticipationID === this.props.participationId))})
        }
      },
      error => this.handleAPIError(error, { loading: false, userInfo: undefined })
    )
    if (this.props.isUser && !this.props.isSiteAdmin) {
      this.roleSecurityCheckSubscription = this.roleID$.pipe(
        switchMap(roleID => concat(
          of(false),
          RoleOrderSecurityCheck(roleID).pipe(
            catchError(_ => of(false))
          )))
      ).subscribe(
        result => this.setState({ canShowDetailsToPerson: result }),
        error => this.handleAPIError(error, { canShowDetailsToPerson: false })
      )
    }
    if (this.props.show && !!this.props.LoginName) {
      this.loginName$.next(this.props.LoginName)
    }

    if (this.props.isPortalAdmin || this.props.isCompanyAdmin) {
      this.companiesSubscription = AutoFilterCompaniesGet("").subscribe(
        (results: AutoFilterCompany[]) => {
          if (this.props.isCompanyAdmin && results.length > 0) {
            this.loadAllLocations(results[0], undefined)
          }
          this.setState({ companies: results })
        }
      )
    }
  }

  loadAllLocations = (company?: AutoFilterCompany, region?: AutoFilterRegion) => {
    this.locationsSubscription = AutoFilterLocationsGet(company?.AccountID || 0, region?.Code || "", "").subscribe(
      data => {
        this.setState({ locations: data, selectedLocation: undefined })
      }
    )
  }

  handleLocationChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    let location = (this.state.locations || []).find(l => l.AccountID === Number(event.target.value))
    this.setState({ selectedLocation: location })
  }

  componentWillUnmount() {
    this.saveSubscription?.unsubscribe();
    this.userRevokeSubscription?.unsubscribe();
    this.userPasswordResetSubscription?.unsubscribe();
    this.loadUserSubscription?.unsubscribe();
    this.roleSecurityCheckSubscription?.unsubscribe();
    this.timeZoneSubscription?.unsubscribe();
    this.userRolesSubscription?.unsubscribe();
    this.existSubscription?.unsubscribe();
    this.companiesSubscription?.unsubscribe();
    this.locationsSubscription?.unsubscribe();
    this.removeRoleSubscription?.unsubscribe();
  }

  componentDidUpdate(prevProps: UserInfoProps, prevState: UserInfoState) {
    if (!this.props.show && prevProps.show) {
      this.timeZoneSubscription.add(
        TimeZoneListGet().subscribe(
          (results: TimeZoneData[]) => this.setState({ timeZones: results }),
          () => { }
        )
      )
      this.setState({ ...this.emptyState })
    }
    if (this.props.show) {
      if (!!this.props.LoginName && prevProps.LoginName !== this.props.LoginName) {
        this.loginName$.next(this.props.LoginName)
      }
    }
    if (!!this.state.userInfo && this.state.userInfo?.RoleID !== prevState.userInfo?.RoleID) {
      this.roleID$.next(this.state.userInfo.RoleID)
    }
    if (this.props.timeZoneId !== prevProps.timeZoneId) {
      this.setState({ timeZoneId: this.props.timeZoneId === 0 ? this.state.timeZones[0].TimeZoneID : this.props.timeZoneId })
    }
  }

  performResetLoginAttempts = () => {
    this.setState({ performingResetLoginAttempts: true })
    ResetLoginAttempts(this.props.LoginName || "").subscribe(
      data => {
        this.setState({ performingResetLoginAttempts: false })
        this.props.onClose && this.props.onClose(true)
        toast.success(data)
      },
      error => this.handleAPIError(error, { performingResetLoginAttempts: false })
    )
  }

  performRemoveDuplicateRoles = () => {
    this.setState({ performingRemoveDuplicateRoles: true })
    RemoveDuplicateRoles(this.props.LoginName || "").subscribe(
      data => {
        this.setState({ performingRemoveDuplicateRoles: false })
        this.props.onClose && this.props.onClose(true)
        toast.success(data)
      },
      error => this.handleAPIError(error, { performingRemoveDuplicateRoles: false })
    )
  }

  performDeleteUser = () => {
    this.setState({ performingDeleteUser: true })
    DeleteUser(this.props.LoginName || "").subscribe(
      data => {
        this.setState({ performingDeleteUser: false })
        this.props.onClose && this.props.onClose(true)
        toast.success(data)
      },
      error => this.handleAPIError(error, { performingDeleteUser: false })
    )
  }

  performDeleteRole = () => {
    this.setState({ performingDeleteUser: true })
    this.removeRoleSubscription = DeleteRole(this.state.userInfo?.ApplicationUserID || 0, this.props.participationId || 0).subscribe(
      data => {
        this.setState({ performingDeleteUser: false })
        this.props.onClose && this.props.onClose(true)
        toast.success(data)
      },
      error => this.handleAPIError(error, { performingDeleteUser: false, showDeleteRoleConfirmation: false })
    )
  }

  mapUserInfo = (info?: UserInformation): UserInfoData => {
    if (!info) {
      return { ...this.emptyData }
    }

    const email = info.AssociatedEmail.filter(email => email.Active)[0]?.ContactDetail || ""
    const phone = FormatPhone(info?.AssociatedPhone.filter(phone => phone.Active)[0]?.ContactDetail || "")

    return {
      userInfo: info!,
      firstName: info.FirstName,
      lastName: info.LastName,
      email: email,
      phone: phone,
      roleId: info.RoleID,
      inactive: !info.Active,
      showNotifications: info.IsNotificationEnabled,
      checkedNotificationServiceIDs: info.NotificationServiceIDs,
      checkedNotificationEventIDs: info.NotificationEventIDs,
      checkedNotificationMethodIDs: info.NotificationMethodIDs
    }
  }

  isNameValid = (value?: string) => !!value && value.trim().length > 0
  isEmailValid = (value: string) => EmailRegexp.test(value)
  isPhoneValid = (value: string) => PhoneRegexp.test(value)

  handleFirstNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ firstName: value, firstNameValid: this.isNameValid(value) })
  }

  handleLastNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    this.setState({ lastName: value, lastNameValid: this.isNameValid(value) })
  }

  handleRoleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const role = Number(event.target.value)
    this.setState({ roleId: role, roleValid: role > 0 })
  }

  handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const phoneIndex = this.state.userInfo?.AssociatedPhone.findIndex(phone => phone.Active) || 0
    const items = this.state.userInfo?.AssociatedPhone || []
    const newPhoneArray: AssociatedData[]  = [
      ...items.slice(0, phoneIndex),
      {
        ...items[phoneIndex],
        ContactDetail: value.replace(/-/g, ""),
      },
      ...items.slice(phoneIndex + 1)
    ]
    this.setState({ phone: value, userInfo: {...this.state.userInfo, AssociatedPhone: newPhoneArray } as UserInformation })
  }

  handlePhoneChangeByEditModal = (data: AssociatedData[]) => {
    const newActivePhone = FormatPhone(data.find(phone => phone.Active)?.ContactDetail || "")

    this.setState(prev => ({
      ...prev,
      showAdditionalPhone: false,
      phone: newActivePhone !== "" && prev.phone !== newActivePhone ? newActivePhone : prev.phone,
      userInfo: { ...prev.userInfo, AssociatedPhone: data || [] } as UserInformation
    }))
  } 

  isLocationAdministrator = () => {
    let isLocationAdmin = false
    if (this.state.roleId > 0) {
      isLocationAdmin = this.state.roles?.find(r => r.Title === 'Location Administrator' && r.RoleID === this.state.roleId) !== undefined
    }
    return isLocationAdmin
  }

  handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    this.setState({
      email: value,
      emailValid: this.isEmailValid(value),
      emailChecked: false,
      emailAvailable: false
    })
  }

  handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!this.props.showNewUser) {
      return true
    }
    const email = e.target.value
    if (this.isEmailValid(email)) {
      const requestParams = this.CreateCheckUserExistsParameters()
      requestParams.UserName = email
      this.existSubscription = CheckUserExists(requestParams).subscribe(
        (data: CheckUserExistsResult) => {
            const emailAvailable = (data.UserExists === 0) && (data.ContactExists === 0)
          this.setState({ emailAvailable: emailAvailable, emailChecked: true })
        },
        (error: Error | APIError) => {
          this.setState({ emailAvailable: false, emailChecked: false })
          error && toast.error((error as APIError)?.errorData?.message || error.message)
        }
      )
    }
  }

  handleInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => this.setState({ inactive: e.target.checked })

  handleCompanyChange = (selected: AutoFilterCompany) => {
    this.setState({ selectedCompany: selected })
    this.loadAllLocations(selected, undefined)
  }

  CreateCheckUserExistsParameters = () : CheckUserExistsParameters =>{
        const email = this.state.email
        const implementationId = 0
    const companyId = this.state.selectedCompany?.AccountID || 0
    const locationId = this.isLocationAdministrator() ? this.state.selectedLocation?.AccountID || 0 : 0
    const roleId = this.state.roleId

    const result: CheckUserExistsParameters = {
        UserName: this.state.email,
        ImplementationID: implementationId,
        CompanyID: companyId,
        LocationID: locationId,
        RoleID: roleId,
        Link: ""
    };
    return result
  }

  isValid = (existingUser: boolean = false) => {
    const { firstName, lastName, email, phone, roleId, selectedCompany } = this.state
    const validity: UserInfoValidity = {
      lastNameValid: existingUser || this.isNameValid(lastName),
      firstNameValid: existingUser || this.isNameValid(firstName),
      emailValid: this.isEmailValid(email),
      phoneValid: existingUser || this.isPhoneValid(phone),
      roleValid: roleId > 0,
      companyValid: !this.props.isPortalAdmin || !this.props.showNewUser || !!selectedCompany
    }
    return Object.values(validity).every((valid: boolean) => valid)
  }

  handleSave = () => {
    const { firstName, lastName, email, phone, roleId, timeZoneId, inactive, emailChecked, emailAvailable } = this.state
    const { isUser, isSiteAdmin, showNewUser } = this.props
    const emailExists = emailChecked && !emailAvailable && showNewUser

    if (!this.isValid(emailExists)) {
      return
    }
    const requestParams = this.CreateCheckUserExistsParameters()
    this.setState({ saving: true })
    this.existSubscription = CheckUserExists(requestParams).subscribe(
        (data: CheckUserExistsResult) => {
            const isUserValid = (data.UserExists === 0)
            if(isUserValid)
            {
                this.saveUser()
            }
            else
            {
                toast.error(data.Error)
            }
            this.setState({ saving: false })
         },
        (error: Error | APIError) => {
            error && toast.error((error as APIError)?.errorData?.message || error.message)
            this.setState({ saving: false })
        }
    )
  }

  saveUser = () => {
    const { firstName, lastName, email, phone, roleId, timeZoneId, inactive, emailChecked, emailAvailable } = this.state
    const { isUser, isSiteAdmin, showNewUser } = this.props
    const emailExists = emailChecked && !emailAvailable && showNewUser

    if (!this.isValid(emailExists)) {
      return
    }
    this.setState({ saving: true })
    const { userInfo, showNotifications, checkedNotificationServiceIDs, checkedNotificationEventIDs, checkedNotificationMethodIDs } = this.state
    const isCollectorActive = (userInfo?.AssociatedRole || []).some(role => role.Active === true && role.Description === 'Collector')

    const emailData: AssociatedData = { ...EmptyAssociatedData, ContactDetail: email, Active: true, DeliveryType: AssociatedDataType.Email }
    const phoneData: AssociatedData = { ...EmptyAssociatedData, ContactDetail: phone.replace(/-/g, ""), Active: true, DeliveryType: AssociatedDataType.Phone }
    const role = this.state.roles?.find(role => role.RoleID === roleId)
    const roleData: RoleData = { Active: true, Description: role?.Description || "", Level: role?.Level || 0, RoleID: role?.RoleID || 0, Title: role?.Title || "" }

    const updateData: UpdateUserDetailParams = {
      Inactive: inactive,
      ApplicationUserID: userInfo?.ApplicationUserID || 0,
      UserName: showNewUser ? email : userInfo?.UserName || "",
      FirstName: firstName || "",
      LastName: lastName || "",
      ModifiedBy: this.props.UserName || "",
      ParticipationID: userInfo?.ParticipationID || 0,
      EmailAddressList: showNewUser ? [emailData] : userInfo?.AssociatedEmail.filter(data => data.ContactDetail !== "") || [],
      PhoneNumberList: showNewUser ? [phoneData] : userInfo?.AssociatedPhone.filter(data => data.ContactDetail !== "") || [],
      RoleList: showNewUser ? [roleData] : userInfo?.AssociatedRole || [],
      NotificationEventIDs: showNotifications ? checkedNotificationEventIDs : [],
      NotificationServiceIDs: showNotifications ? checkedNotificationServiceIDs : [],
      NotificationMethodIDs: showNotifications ? checkedNotificationMethodIDs : [],
      Link: userInfo?.Link || "",
      IsUser: this.props.mode === UserInfoMode.Personal && (isUser && !isSiteAdmin) || false,
      IsNotificationEnabled: showNotifications,
      isCollectorActive: isCollectorActive || false,
      timeZoneID: timeZoneId || 0,
      AccountID: this.state.selectedCompany?.AccountID || 0,
      LocationID: this.state.selectedLocation?.AccountID || 0
    }

    const SaveMethod = (updateData: UpdateUserDetailParams) => userInfo?.ApplicationUserID ? UserNotificationInfoUpdate(updateData) : CreateUser(updateData)

    this.saveSubscription && this.saveSubscription.unsubscribe()
    this.saveSubscription = SaveMethod(updateData).subscribe(
      data => {
        this.setState({ saving: false })
        toast.success(data)
        if (this.props.showNewUser) {
          this.props.onClose && this.props.onClose(true)
        } else {
          if (this.props.mode === UserInfoMode.Personal) {
            this.state.userInfo && this.loginName$.next(this.state.userInfo.UserName)
          } else {
            this.props.onClose && this.props.onClose(true)
          }
        }
      },
      error => this.handleAPIError(error, { saving: false })
    )
  }

  performRevoke = () => {
    this.setState({ revoking: true, showRevokeConfirmation: false })
    this.userRevokeSubscription && this.userRevokeSubscription.unsubscribe()
    this.userRevokeSubscription = UserRevoke(this.state.userInfo?.ApplicationUserID || 0).subscribe(
      data => {
        this.setState({ revoking: false })
        toast.success(data)
        this.props.onClose && this.props.onClose(true)
      },
      error => this.handleAPIError(error, { revoking: false })
    )
  }

  performPasswordReset = () => {
    this.setState({ resettingPassword: true, showResetPasswordConfirmation: false })
    this.userPasswordResetSubscription && this.userPasswordResetSubscription.unsubscribe()
    this.userPasswordResetSubscription = UserPasswordReset(this.props.LoginName || "", this.state.userInfo?.Link || "").subscribe(
      data => {
        this.setState({ resettingPassword: false })
        toast.success(data)
      },
      error => this.handleAPIError(error, { resettingPassword: false })
    )
  }

  performClose = () => this.props.onClose && this.props.onClose(false)

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error((error as APIError)?.errorData?.message || error.message)
  }

  handleChangeNotificationService = (notificationServiceId: number, value: boolean) => {
    this.setState((prev) => ({...prev, checkedNotificationServiceIDs: value
      ? [...prev.checkedNotificationServiceIDs, notificationServiceId]
      : prev.checkedNotificationServiceIDs.filter(id => id !== notificationServiceId)})
    )
  }

  handleChangeNotificationEvent = (notificationEventId: number, value: boolean) => {
    this.setState((prev) => ({...prev, checkedNotificationEventIDs: value
      ? [...prev.checkedNotificationEventIDs, notificationEventId]
      : prev.checkedNotificationEventIDs.filter(id => id !== notificationEventId)})
    )
  }

  handleChangeNotificationMethod = (notificationMethodId: number, value: boolean) => {
    this.setState((prev) => ({...prev, checkedNotificationMethodIDs: value
      ? [...prev.checkedNotificationMethodIDs, notificationMethodId]
      : prev.checkedNotificationMethodIDs.filter(id => id !== notificationMethodId)})
    )
  }

  handleChangeShowNotifications = (value: boolean) => {
    this.setState((prev) => ({...prev, showNotifications: value}))
  }

  render() {
    const { isCompanyAdmin, isPortalAdmin, isUser, isSiteAdmin, LoginName, showNewUser } = this.props
    const { loading, saving, firstName, firstNameValid, lastName, lastNameValid, roleId, roleValid, email, emailAvailable, emailChecked, emailValid,
      roles, inactive, userInfo, revoking, resettingPassword, canShowDetailsToPerson, showAdditionalPhone, showAdditionalRoles, 
      performingResetLoginAttempts, performingRemoveDuplicateRoles, performingDeleteUser, performingDeleteRole, selectedLocation } = this.state
    const disableButtons = loading || !userInfo
    const { mode } = this.props
    const isPersonal = mode === UserInfoMode.Personal
    const title = isPersonal ? "MY INFORMATION" : "OUR TEAM'S INFORMATION"
    const readonlyUserName = isPersonal || (isUser && !isSiteAdmin)
    const phone = FormatPhone(this.state.phone)
    const phoneValid = isSiteAdmin ? PhoneRegexp.test(phone) : true
    const locationRequired = this.props.isPortalAdmin ? "required" : ""
    const isShowAdditionalRole = !isPersonal && !isCompanyAdmin && !isPortalAdmin
    const emailExists = emailChecked && !emailAvailable && showNewUser
    const disableSaveButton = !emailExists ? loading || !this.isValid() || (!emailAvailable && showNewUser) || !phoneValid : loading || !this.isValid(true)
    const roleID = this.props.roleId || this.state.roleId
    
    const formRows = <>
      {isSiteAdmin && <Form.Group as={Row}>
        <Form.Label column lg={3}>Failed Login Attempts: {this.props.LoginAttempts} of {MaxFailedLoginAttempts}</Form.Label>
        <Col lg={3}>
          <ActionButton className="fixed-w10" onClick={() => this.setState({ showResetLoginAttemptsConfirmation: true })} spin={performingResetLoginAttempts} disableOnSpin>Reset</ActionButton>
        </Col>
      </Form.Group>}
      {isSiteAdmin && <Form.Group as={Row}>
        <Form.Label column lg={3}>Duplicate Roles</Form.Label>
        <Col lg={3}>
          <ActionButton className="fixed-w10" onClick={() => this.setState({ showRemoveDuplicateRolesConfirmation: true })} spin={performingRemoveDuplicateRoles} disableOnSpin>Remove</ActionButton>
        </Col>
      </Form.Group>}
      {isSiteAdmin && <Form.Group as={Row}>
        <Form.Label column lg={3}>Delete User</Form.Label>
        <Col lg={3}>
          <ActionButton className="fixed-w10" onClick={() => this.setState({ showDeleteUserConfirmation: true })} spin={performingDeleteUser} disableOnSpin>Delete</ActionButton>
        </Col>
      </Form.Group>}
      {!isPersonal && !emailExists && <Row className="justify-content-end form-group">
        <Col sm={2}>
          <Checkbox id="inactive" label="Inactive" onChange={this.handleInactiveChange} checked={inactive} />
        </Col>
      </Row>}
      {!isPersonal && !showNewUser && <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>Login Name</Form.Label>
        <Col lg={7}>
          <Form.Control type="text" readOnly defaultValue={LoginName} className="bg-white" />
        </Col>
      </Form.Group>}
      <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>User's Email</Form.Label>
        <Col lg={7}>
          {isPersonal && <Form.Control type="text" readOnly={readonlyUserName} defaultValue={userInfo?.UserName} className="bg-white" />}
          {!isPersonal && <Form.Control type="text" id="email" readOnly={!showNewUser} defaultValue={email} className="bg-white" onChange={this.handleEmailChange} onBlur={this.handleEmailBlur} isInvalid={!emailValid} />}
          {showNewUser && <>
            {!emailValid && <Form.Control.Feedback type="invalid" className="d-block">Please enter valid email</Form.Control.Feedback>}
            {emailChecked && emailAvailable && < Form.Control.Feedback type="valid" className="d-block">Email is available</Form.Control.Feedback>}
          </>}
        </Col>
      </Form.Group>
      {!emailExists && <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>Personal Phone</Form.Label>
        <Col lg={7}>
          {isSiteAdmin || showNewUser && <Form.Control type="text" as={MaskedInput} mask="111-111-1111" value={phone} onChange={this.handlePhoneChange} className="bg-white" isInvalid={!phoneValid} />}
          {!isSiteAdmin && !showNewUser && <Form.Control type="text" readOnly defaultValue={phone} value={phone} className="bg-white" />}
        </Col>
        <Col lg={3} className="pt-1 pt-lg-0">
          <Button variant="secondary" onClick={() => this.setState({ showAdditionalPhone: true })} disabled={disableButtons} className="fixed-w10">Edit Phone Number</Button>
        </Col>
      </Form.Group>}
      {!emailExists && <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>First Name</Form.Label>
        <Col lg={7}>
          <Form.Control type="text" value={firstName} className="bg-white" onChange={this.handleFirstNameChange} isInvalid={!firstNameValid} />
        </Col>
        {!isPersonal && <Col lg={3} className="pt-1 pt-lg-0">
          <ActionButton variant="secondary" disabled={disableButtons} className="fixed-w10" onClick={() => this.setState({ showResetPasswordConfirmation: true })} spin={resettingPassword} disableOnSpin>Reset Password</ActionButton>
        </Col>}
      </Form.Group>}
      {!emailExists && <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>Last Name</Form.Label>
        <Col lg={7}>
          <Form.Control type="text" value={lastName} className="bg-white" onChange={this.handleLastNameChange} isInvalid={!lastNameValid} />
        </Col>
      </Form.Group>}
      <Form.Group as={Row} className="required">
        <Form.Label column lg={2}>User's Role(s)</Form.Label>
        <Col lg={7}>
          {!showNewUser && <Form.Control type="text" readOnly defaultValue={userInfo?.AssociatedRole && userInfo?.AssociatedRole.length > 0 ? userInfo?.AssociatedRole.find(i => i.RoleID === roleID)?.Title  : ""} className="bg-white" />}
          {showNewUser && <Form.Control id="role" as="select" onChange={this.handleRoleChange} value={roleId} isInvalid={!roleValid} className="bg-white" >
            <option value="">Select</option>
            {roles?.map((role: Role) => <option value={role.RoleID} key={role.RoleID}>{role.Title}</option>)}
          </Form.Control>}
        </Col>
        {isShowAdditionalRole && <Col lg={3} className="pt-1 pt-lg-0">
          <Button variant="secondary" onClick={() => this.setState({ showAdditionalRoles: true })} disabled={disableButtons} className="fixed-w10">Additional Role</Button>
        </Col>}
        {!isPersonal && (isCompanyAdmin || isPortalAdmin) &&
        <Col lg={3}>
          <ActionButton className="fixed-w10" onClick={() => this.setState({ showDeleteRoleConfirmation: true })} spin={performingDeleteRole} disableOnSpin>Delete Role</ActionButton>
        </Col>}
      </Form.Group>
      {showNewUser && this.props.isPortalAdmin && <Form.Group as={Row} className="required">
        <Form.Label column sm={2} htmlFor="company">Company</Form.Label>
        <Col sm={7}>
          <CompanyInput onChange={items => this.handleCompanyChange(items[0])} placeholder="Choose a company..." />
        </Col>
      </Form.Group>}
      {showNewUser && ((this.props.isPortalAdmin && this.isLocationAdministrator()) || !this.props.isPortalAdmin) && <Form.Group as={Row} className={locationRequired}>
        <Form.Label column sm={2} htmlFor="Location">Locations</Form.Label>
        <Col sm={7}>
          <Form.Control id="location" as="select" value={selectedLocation?.AccountID} onChange={this.handleLocationChange}>
            <option value="">Select</option>
            {(this.state.locations || []).map(l => <option value={l.AccountID} key={l.AccountID}>{l.Name}</option>)}
          </Form.Control>
        </Col>
      </Form.Group>}

      {isPersonal && (isCompanyAdmin || (!isSiteAdmin && canShowDetailsToPerson)) && <Form.Group as={Row}>
        <Col>
          <span>Control who can view Orders placed by other users</span>
          <Button variant="primary" disabled={disableButtons} onClick={() => this.setState({ showOrderSecurityOverride: true })} className="ml-2">Details to Person</Button>
        </Col>
      </Form.Group>}
    </>
    const modalContent = <>
    <Modal.Header closeButton={!isPersonal}>
      <Modal.Title>{title}</Modal.Title>
    </Modal.Header>
      <Modal.Body>
        <Form>
          {loading && <Row><Col className="text-center"><Spinner animation="border" variant="secondary" /></Col></Row>}
          {!loading && formRows}
          {!loading && !emailExists && ((isSiteAdmin && !isPersonal) || !isSiteAdmin) && (
            <NotificationSelection
              participationId={this.state.userInfo?.ParticipationID}
              showNotifications={this.state.showNotifications}
              canViewOrderTrackingGrid={this.state.userInfo?.UserAccessViewModel?.CanViewOrderTrackingGrid}
              checkedNotificationServiceIDs={this.state.checkedNotificationServiceIDs}
              checkedNotificationEventIDs={this.state.checkedNotificationEventIDs}
              checkedNotificationMethodIDs={this.state.checkedNotificationMethodIDs}
              onCheckNotificationService={this.handleChangeNotificationService}
              onCheckNotificationEvent={this.handleChangeNotificationEvent}
              onCheckNotificationMethod={this.handleChangeNotificationMethod}
              onCheckShowNotifications={this.handleChangeShowNotifications}
            />
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {mode === UserInfoMode.Team && <>
          {false && <ActionButton variant="secondary" disabled={disableButtons} onClick={() => this.setState({ showRevokeConfirmation: true })} spin={revoking} disableOnSpin className="mr-2 fixed-w5">Revoke</ActionButton>}
          <Button variant="secondary" onClick={this.performClose} className="mr-2 fixed-w5">Cancel/Close</Button>
        </>}
        {isPersonal && <>
          <Button variant="secondary" onClick={this.performClose} className="mr-2 fixed-w5">Cancel</Button>
        </>}
        <ActionButton variant="primary" className="fixed-w5" disabled={disableSaveButton} onClick={this.handleSave} spin={saving} disableOnSpin>{this.props.showNewUser ? 'Save' : 'Update'}</ActionButton>
      </Modal.Footer>
    </>

    return <>
      <AdditionalAssociatedDataModal data={userInfo?.AssociatedPhone || []} type={AssociatedDataType.Phone} show={showAdditionalPhone} onSave={this.handlePhoneChangeByEditModal} onClose={() => this.setState({ showAdditionalPhone: false })} />
      <AdditionalRoleModal
        onSaveTimeZoneId={timeZoneId => this.setState({ timeZoneId: timeZoneId })}
        timeZones={this.state.timeZones}
        timeZoneId={this.props.timeZoneId}
        data={userInfo?.AssociatedRole || []}
        show={showAdditionalRoles}
        onSave={data => this.setState({ showAdditionalRoles: false, userInfo: { ...this.state?.userInfo, AssociatedRole: data || [] } as UserInformation })}
        onClose={() => this.setState({ showAdditionalRoles: false })}
        userInfo={userInfo}
      />
      {isPersonal && <Modal.Dialog size="xl">{modalContent}</Modal.Dialog>}
      {!isPersonal && <Modal backdrop="static" size="xl" show={this.props.show} onHide={this.performClose}>{modalContent}</Modal>}
      <ConfirmationModal show={this.state.showRevokeConfirmation}
        title="CONFIRM REVOKE"
        textTitle="Are you sure you want to revoke?"
        text=""
        onConfirm={this.performRevoke}
        onHide={() => this.setState({ showRevokeConfirmation: false })} />
      <ConfirmationModal show={this.state.showResetPasswordConfirmation}
        title="CONFIRM PASSWORD RESET"
        textTitle="Are you sure you want to continue?"
        text=""
        onConfirm={this.performPasswordReset}
        onHide={() => this.setState({ showResetPasswordConfirmation: false })} />
      {this.state.showResetLoginAttemptsConfirmation && <ConfirmationModal show
        title="CONFIRM RESET LOGIN ATTEMPTS"
        textTitle="Are you sure you want to continue?"
        text="All login attempts for this user will be reset"
        onConfirm={this.performResetLoginAttempts}
        onHide={() => this.setState({ showResetLoginAttemptsConfirmation: false })} />}
      {this.state.showRemoveDuplicateRolesConfirmation && <ConfirmationModal show
        title="CONFIRM REMOVE DUPLICATE ROLES"
        textTitle="Are you sure you want to continue?"
        text="Roles will be reset to the initial state"
        onConfirm={this.performRemoveDuplicateRoles}
        onHide={() => this.setState({ showRemoveDuplicateRolesConfirmation: false })} />}
      {this.state.showDeleteUserConfirmation && <ConfirmationModal show
        title="CONFIRM DELETE USER"
        textTitle="Are you sure you want to continue?"
        text="All user data will be erased"
        onConfirm={this.performDeleteUser}
        onHide={() => this.setState({ showDeleteUserConfirmation: false })} />}
      {this.state.showDeleteRoleConfirmation && <ConfirmationModal show
        title="CONFIRM DELETE USER ROLE"
        textTitle="Are you sure you want to continue?"
        text="User role will be removed if it is not the only role for this user"
        onConfirm={this.performDeleteRole}
        onHide={() => this.setState({ showDeleteRoleConfirmation: false })} />}
      <OrderSecurityOverride show={this.state.showOrderSecurityOverride}
        userInfo={this.state.userInfo}
        onClose={() => this.setState({ showOrderSecurityOverride: false })} />
    </>
  }
}

function mapStateToProps(state: AppState): UserInfoAuthProps {
  return {
    isLocationAdmin: isLocationAdmin(state),
    isCompanyAdmin: isCompanyAdmin(state),
    isPortalAdmin: isPortalAdmin(state),
    isSiteAdmin: isSiteAdmin(state),
    isUser: isUser(state),
    UserName: getUserNameForSelectedCompany(state),
    PrimaryRoleID: RoleIdForSelectedCompany(state),
  }
}
const ConnectedUserInfo = connect(mapStateToProps, null)(UserInfo)
export default ConnectedUserInfo
